import { Box, Button, FormControlLabel, Switch, Typography } from "@mui/material";
import { Supplier } from "../types/api";
import { useContext, useEffect, useState } from "react";
import { getSuppliers, createSupplier, updateSupplier, searchSuppliers, deleteSupplier, archiveSupplier, unarchiveSupplier } from "src/services/supplier.service";
import Search from "src/components/search.component";
import SupplierTable from "src/components/supplier-table.component";
import { CreateSupplierModal } from "src/components/modal.create-supplier";
import { useStickyState } from "src/util";
import GlobalMessageContext from "src/context/globalMessage.context";

const SuppliersView = () => {
  const [supplierData, setSupplierData] = useState<Supplier[]>([]);
  const [currentSkip, setCurrentSkip] = useStickyState<number>(0, "supplier.currentSkip");
  const [currentTake, setCurrentTake] = useStickyState<number>(25, "supplier.currentTake");
  const [totalRows, setTotalRows] = useStickyState<number>(0, "supplier.totalRows");
  const [loading, setLoading] = useState<boolean>(false);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [searchTerm, setSearchTerm] = useStickyState<string>("", "supplier.searchTerm");
  const [showArchived, setShowArchived] = useStickyState<boolean>(true, "supplier.showArchived");
  const uxMessage = useContext(GlobalMessageContext);
  const activeOnly = !showArchived;

  useEffect(() => {
    // If search term is set then don't do a full get for suppliers
    if (searchTerm === undefined || searchTerm === "") {
      setLoading(true);
      getSuppliers(
        { skip: currentSkip, take: currentTake, activeOnly },
        (data) => {
          setLoading(false);
          setSupplierData(data);
        },
        (error) => {
          setLoading(false);
          console.error(error);
          setSupplierData([]);
          uxMessage.setMessage({
            message:
              "Sorry - we seem to be having some troubles right now... please try again later!",
            severity: "error",
          });
        },
        setTotalRows
      );
    }
  }, [currentSkip, currentTake, uxMessage, activeOnly]);

  const setSkip = (page: number) => {
    setCurrentSkip(page * currentTake);
  };

  const setTake = (pageSize: number) => {
    if (pageSize !== currentTake) {
      // Only recalculate skip if page size actually changed
      const oldTake = currentTake;
      setCurrentTake(pageSize);
      setCurrentSkip(Math.trunc(currentSkip * (oldTake / pageSize)));
    }
  };

  const refreshSuppliers = () => {
    setLoading(true);
    getSuppliers(
      { skip: currentSkip, take: currentTake, activeOnly },
      (data) => {
        setLoading(false);
        setSupplierData(data);
      },
      (error) => {
        setLoading(false);
        console.error(error);
        setSupplierData([]);
        uxMessage.setMessage({
          message:
            "Sorry - we seem to be having some troubles right now... please try again later!",
          severity: "error",
        });
      },
      setTotalRows
    );
  };

  const handleCreateSupplier = (supplier: Supplier) => {
    createSupplier(
      supplier,
      (data) => {
        setCreateModalOpen(false);
        uxMessage.setMessage({
          message: `Supplier "${data.name}" created successfully!`,
          severity: "success",
        });
        refreshSuppliers();
      },
      (error) => {
        console.error(error);
        uxMessage.setMessage({
          message: "Failed to create supplier. Please try again.",
          severity: "error",
        });
      }
    );
  };

  const handleUpdateSupplier = (supplier: Supplier) => {
    updateSupplier(
      supplier,
      (data) => {
        setEditModalOpen(false);
        setEditingSupplier(null);
        uxMessage.setMessage({
          message: `Supplier "${data.name}" updated successfully!`,
          severity: "success",
        });
        refreshSuppliers();
      },
      (error) => {
        console.error(error);
        uxMessage.setMessage({
          message: "Failed to update supplier. Please try again.",
          severity: "error",
        });
      }
    );
  };

  // Reload whatever the list is currently showing: the search results or the page.
  const reloadList = () => {
    if (searchTerm && searchTerm !== "") {
      searchSuppliers({ search: searchTerm, activeOnly }, setSupplierData, setTotalRows);
    } else {
      refreshSuppliers();
    }
  };

  const handleArchiveSupplier = (supplier: Supplier) => {
    if (!supplier.guid) return;
    archiveSupplier(
      supplier.guid,
      () => {
        uxMessage.setMessage({ message: `Supplier "${supplier.name}" archived`, severity: "success" });
        reloadList();
      },
      (error) => {
        console.error(error);
        uxMessage.setMessage({
          message: error?.response?.data?.message || "Failed to archive supplier. Please try again.",
          severity: "error",
        });
        reloadList();
      }
    );
  };

  const handleUnarchiveSupplier = (supplier: Supplier) => {
    if (!supplier.guid) return;
    unarchiveSupplier(
      supplier.guid,
      () => {
        uxMessage.setMessage({ message: `Supplier "${supplier.name}" unarchived`, severity: "success" });
        reloadList();
      },
      (error) => {
        console.error(error);
        uxMessage.setMessage({
          message: error?.response?.data?.message || "Failed to unarchive supplier. Please try again.",
          severity: "error",
        });
        reloadList();
      }
    );
  };

  const handleDeleteSupplier = (supplier: Supplier) => {
    if (!supplier.guid) return;
    deleteSupplier(
      supplier.guid,
      () => {
        uxMessage.setMessage({
          message: `Supplier "${supplier.name}" deleted`,
          severity: "success",
        });
        reloadList();
      },
      (error) => {
        console.error(error);
        uxMessage.setMessage({
          message:
            error?.response?.data?.message ||
            "Failed to delete supplier. Please try again.",
          severity: "error",
        });
        reloadList();
      }
    );
  };

  const handleRowClick = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setEditModalOpen(true);
  };

  const doSearch = (st: string) => {
    setSearchTerm(st);
    searchSuppliers({ search: st, activeOnly }, setSupplierData, setTotalRows);
  };

  const onReset = () => {
    setSearchTerm("");
    setLoading(true);
    getSuppliers(
      { skip: currentSkip, take: currentTake, activeOnly },
      (data) => {
        setLoading(false);
        setSupplierData(data);
      },
      (error) => {
        setLoading(false);
        console.error(error);
        setSupplierData([]);
        uxMessage.setMessage({
          message:
            "Sorry - we seem to be having some troubles right now... please try again later!",
          severity: "error",
        });
      },
      setTotalRows
    );
  };

  return (
    <>
      <Typography variant="h1">Suppliers</Typography>
      <Box sx={{ m: 1, textAlign: "right" }}>
        <Button
          variant="contained"
          size="small"
          color="inherit"
          onClick={() => setCreateModalOpen(true)}
        >
          Create Supplier
        </Button>
      </Box>
      <Box sx={{ m: 1 }}>
        <div style={{ textAlign: "center" }}>
          <Search
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            doSearch={(st: string) => doSearch(st)}
            clearOnEnter={false}
            onReset={onReset}
            placeholderText="Search suppliers by name or sales rep"
          />
        </div>
      </Box>
      <Box sx={{ m: 1, display: "flex", justifyContent: "flex-end" }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showArchived}
              onChange={(e) => {
                setCurrentSkip(0);
                setShowArchived(e.target.checked);
              }}
            />
          }
          label="Show archived"
        />
      </Box>
      <Box sx={{ m: 1 }}>
        <SupplierTable
          data={supplierData}
          pageSize={currentTake}
          totalRows={totalRows}
          currentPage={Math.floor(currentSkip / currentTake)}
          loading={loading}
          onPageChange={setSkip}
          onPageSizeChange={setTake}
          onRowClick={handleRowClick}
          onDelete={handleDeleteSupplier}
          onArchive={handleArchiveSupplier}
          onUnarchive={handleUnarchiveSupplier}
        />
      </Box>
      <CreateSupplierModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={handleCreateSupplier}
      />
      <CreateSupplierModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingSupplier(null);
        }}
        onSave={handleUpdateSupplier}
        editMode={true}
        initialData={editingSupplier || undefined}
      />
    </>
  );
};

export default SuppliersView;
